// Definiciones del formulario de pre-consulta de lactancia
// Cada paso tiene un título, subtítulo y una lista de preguntas.

export const lactanciaSteps = [
  {
    id: 'bienvenida',
    type: 'intro',
    title: 'Bienvenida',
    subtitle: 'Pre-consulta de lactancia',
    intro:
      'Este es un espacio para contarme tu historia antes de nuestro encuentro. No hay respuestas correctas o incorrectas — lo que recuerdes está bien. Lleva unos 10-15 minutos y podés volver más tarde si necesitás pausarlo.',
    intro2:
      'Llenarlo nos permite liberar entre 20 y 30 minutos de la consulta presencial para dedicarlos a resolver lo que realmente necesitás, sin gastar ese tiempo en preguntas iniciales. Gracias por tomarte este ratito 🌿',
    note: 'Tus respuestas se guardan automáticamente en tu navegador hasta que las envíes.',
  },
  {
    id: 'sobre-vos',
    title: 'Empecemos por vos',
    subtitle: 'Para conocernos un poquito',
    fields: [
      { key: 'nombre', label: '¿Cómo te llamás?', type: 'text', placeholder: 'Tu nombre', required: true },
      { key: 'edadMama', label: '¿Cuántos años tenés?', type: 'text', placeholder: 'Edad', required: true },
      {
        key: 'momentoBebe',
        label: '¿En qué momento estás?',
        helper: 'Contame si esperás a tu bebé (semana de embarazo) o cuántos días / semanas / meses tiene.',
        type: 'text',
        placeholder: 'Ej: 32 semanas de embarazo · 15 días · 3 meses',
        required: true,
      },
    ],
  },
  {
    id: 'embarazo',
    title: 'Pensando en tu embarazo',
    subtitle: 'Lo que sentís importante compartir',
    fields: [
      {
        key: 'embarazoComplicaciones',
        label: '¿Hubo algo médico o emocional relevante durante el embarazo?',
        helper: 'Lo que sientas que vale la pena que sepa.',
        type: 'textarea',
        placeholder: 'Contame con tus palabras…',
      },
      {
        key: 'controlesIndicaciones',
        label: '¿Tuviste controles prenatales regulares?',
        helper: '¿Te dieron alguna indicación específica respecto a la lactancia?',
        type: 'textarea',
        placeholder: 'Ej: controles mensuales, sin indicaciones / me hablaron de…',
      },
      {
        key: 'preparacionLactancia',
        label: '¿Recibiste información o preparación para amamantar?',
        helper: 'Curso, charla, libro, redes, ninguna…',
        type: 'textarea',
        placeholder: 'Contame qué leíste, escuchaste o aprendiste',
      },
    ],
  },
  {
    id: 'nacimiento',
    title: 'El nacimiento de tu bebé',
    subtitle: 'Cómo fue ese momento',
    fields: [
      {
        key: 'tipoParto',
        label: '¿Cómo fue el nacimiento?',
        type: 'radio',
        options: [
          'Vaginal espontáneo',
          'Vaginal con inducción',
          'Cesárea programada',
          'Cesárea de urgencia',
          'Vaginal con instrumental (fórceps / ventosa)',
          'Otro · todavía no nació',
        ],
      },
      {
        key: 'duracionParto',
        label: '¿Cuándo nació tu bebé?',
        helper: 'O si todavía no nació, ¿cuál es la fecha probable de parto?',
        type: 'text',
        placeholder: 'Ej: 14 de marzo · FPP 20 de junio',
      },
      {
        key: 'comoSentiste',
        label: '¿Cómo te sentiste durante el nacimiento?',
        helper: 'Lo que recuerdes — emociones, cuidados, vivencia.',
        type: 'textarea',
        placeholder: 'Escribí con tranquilidad…',
      },
    ],
  },
  {
    id: 'primeros-momentos',
    title: 'Los primeros momentos',
    subtitle: 'Las primeras horas con tu bebé',
    fields: [
      {
        key: 'pielConPiel',
        label: '¿Hubo contacto piel con piel inmediato?',
        helper: '¿Por cuánto tiempo aproximadamente?',
        type: 'textarea',
        placeholder: 'Ej: sí, una hora seguida · breve · no por cesárea · no recuerdo',
      },
      {
        key: 'primeraToma',
        label: '¿Tu bebé tomó el pecho en la primera hora de vida?',
        type: 'radio',
        options: ['Sí', 'No', 'No recuerdo / no sé', 'Todavía no nació'],
      },
      {
        key: 'complicacionesNeonatales',
        label: '¿Hubo alguna complicación neonatal?',
        helper: 'Internación, ictericia, hipoglucemia, otra.',
        type: 'textarea',
        placeholder: 'Si no hubo, podés escribir “ninguna”',
      },
    ],
  },
  {
    id: 'tu-bebe',
    title: 'Sobre tu bebé',
    subtitle: 'Algunos datos prácticos',
    fields: [
      {
        key: 'aTermino',
        label: '¿Tu bebé nació a término?',
        type: 'radio',
        options: ['Sí, a término (37+ semanas)', 'Prematuro/a', 'Pos-término', 'Todavía no nació'],
      },
      { key: 'pesoNacer', label: 'Peso al nacer', type: 'text', placeholder: 'Ej: 3.250 kg' },
      { key: 'pesoAlta', label: 'Peso al alta', type: 'text', placeholder: 'Ej: 3.100 kg' },
      { key: 'pesoHoy', label: 'Peso hoy (aproximado)', type: 'text', placeholder: 'Ej: 4.500 kg' },
      {
        key: 'suplementos',
        label: '¿Recibió suplementos (fórmula, glucosa, etc.)?',
        helper: 'Si sí, ¿en qué momento y por qué?',
        type: 'textarea',
        placeholder: 'Ej: fórmula al 2º día por bajada de azúcar · ninguno',
      },
    ],
  },
  {
    id: 'lactancia-hoy',
    title: 'Cómo va la lactancia hoy',
    subtitle: 'El día a día con la teta',
    fields: [
      {
        key: 'estaAmamantando',
        label: '¿Estás amamantando actualmente?',
        type: 'radio',
        options: [
          'Sí, exclusiva (solo teta)',
          'Sí, mixta (teta + fórmula)',
          'Sí, con extracción (sacaleche)',
          'Dejé de amamantar',
          'Todavía no empecé',
        ],
      },
      {
        key: 'frecuenciaDia',
        label: '¿Con qué frecuencia mama de día?',
        type: 'text',
        placeholder: 'Ej: cada 2 horas · a demanda · cada 3-4 hs',
      },
      {
        key: 'frecuenciaNoche',
        label: '¿Y durante la noche?',
        type: 'text',
        placeholder: 'Ej: 2-3 veces · no se despierta · cada hora',
      },
      {
        key: 'duracionTomas',
        label: '¿Cuánto duran las tomas aproximadamente?',
        type: 'text',
        placeholder: 'Ej: 15-20 min · varía mucho · 5 minutos',
      },
      {
        key: 'ofreceAmbosPechos',
        label: '¿Le ofrecés ambos pechos en cada toma o uno solo?',
        type: 'radio',
        options: ['Ambos pechos', 'Uno solo', 'Depende del momento', 'No sé bien cómo hacerlo'],
      },
      {
        key: 'panales',
        label: '¿Cuántos pañales moja y ensucia por día?',
        helper: 'Aproximado: orina (mojados) y caquita (manchados).',
        type: 'textarea',
        placeholder: 'Ej: 5-6 mojados, 2 con caquita',
      },
      {
        key: 'chupeteMamadera',
        label: '¿Usa chupete o mamadera?',
        helper: 'Si sí, ¿desde cuándo?',
        type: 'textarea',
        placeholder: 'Ej: chupete desde la 1ª semana · ninguno',
      },
    ],
  },
  {
    id: 'dificultades',
    title: 'Si hay dificultades',
    subtitle: 'No siempre fluye, y eso también merece espacio',
    fields: [
      {
        key: 'hayDolor',
        label: '¿Sentís dolor al amamantar?',
        type: 'radio',
        options: ['No', 'Sí, al principio de la toma', 'Sí, durante toda la toma', 'Sí, entre tomas', 'A veces sí, a veces no'],
      },
      {
        key: 'describirDolor',
        label: 'Si hay dolor, contame cómo es',
        helper: 'Pinchazo, ardor, presión, en qué momento aparece.',
        type: 'textarea',
        placeholder: 'Describilo con tus palabras…',
      },
      {
        key: 'pezones',
        label: '¿Tus pezones tienen grietas, sangrado o cambios de forma o color?',
        type: 'textarea',
        placeholder: 'Ej: una grieta del lado derecho · ninguno',
      },
      {
        key: 'ingurgitacionMastitis',
        label: '¿Tuviste ingurgitación, mastitis u obstrucciones?',
        type: 'textarea',
        placeholder: 'Si no sabés qué es alguno, está bien escribirlo',
      },
    ],
  },
  {
    id: 'historia-cuerpo',
    title: 'Tu historia y tu cuerpo',
    subtitle: 'Información que me ayuda a acompañarte mejor',
    fields: [
      {
        key: 'lactanciasPrevias',
        label: '¿Tenés antecedentes de lactancia previa?',
        helper: '¿Cómo fue esa experiencia?',
        type: 'textarea',
        placeholder: 'Ej: amamanté a mi primer hijo 1 año · es mi primera vez',
      },
      {
        key: 'condicionesMedicas',
        label: '¿Tenés alguna condición médica actual o previa?',
        helper: 'Tiroides, diabetes, SOP, hipertensión, etc.',
        type: 'textarea',
        placeholder: 'Si no, escribí “ninguna”',
      },
      {
        key: 'medicacion',
        label: '¿Tomás medicación actualmente?',
        helper: 'Cualquier medicación, incluso vitaminas o suplementos.',
        type: 'textarea',
        placeholder: 'Ej: levotiroxina, vitamina D · ninguna',
      },
      {
        key: 'cirugias',
        label: '¿Tuviste cirugías mamarias o torácicas?',
        helper: 'Aumento, reducción, biopsia, otra.',
        type: 'textarea',
        placeholder: 'Si no, escribí “ninguna”',
      },
    ],
  },
  {
    id: 'sentir-querer',
    title: 'Lo que sentís y deseás',
    subtitle: 'Esta parte es la más importante',
    fields: [
      {
        key: 'estadoEmocional',
        label: '¿Cómo te estás sintiendo emocionalmente con todo esto?',
        type: 'textarea',
        placeholder: 'Sin filtro: cansancio, miedo, frustración, alegría, todo a la vez…',
      },
      {
        key: 'objetivoLactancia',
        label: '¿Cuál es tu deseo u objetivo con esta lactancia?',
        helper: 'Exclusiva, mixta, una duración específica, no exigirte nada.',
        type: 'textarea',
        placeholder: 'Lo que sientas — no hay objetivo correcto',
      },
      {
        key: 'focoEncuentro',
        label: '¿Hay algo específico que te gustaría trabajar o mejorar en nuestro encuentro?',
        type: 'textarea',
        placeholder: 'Si todavía no lo sabés, también está bien decirlo',
      },
    ],
  },
];
